<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionsConfig"
      :has-return="true"
    />
    <progress-filter
      :academic-years="academicYears"
      :default-value="lastAcademicYear"
      @on-change-academic-year="onChangeAcademicYearHandler"
    />
    <section-title
      class="mb-3 pl-1 ml-1 section-title__extended"
      icon="fas fa-clipboard-list-check"
      title="Resumen de Información General"
      title-class="font-16"
    />
    <general-info-summary
      title=""
      total-title="Total de Estudiantes"
      pending-title="Estudiantes Pendientes"
      completed-title="Por ciento de Estudiantes Completados"
      :total-count="summary.totalCount"
      :pending-count="summary.pendingCount"
      :completed-count="summary.completedPercentage"
    />
    <div class="ml-2 mt-3">
      <summary-collapse
        :is-opened="true"
        :action-summary="actionSummary"
        title="Detalle de Resumen Actual"
      >
        <progress-info-summary :items="currentProgressSummary" />
      </summary-collapse>
    </div>
    <div class="mt-4 ml-2">
      <section-title has-line title="Historial de Informes de Progreso" />
      <progress-info-history
        :items="progressHistory"
        :action-items="actionItems"
        @on-refresh="onRefreshHandler"
        @on-close-ip="onCloseIpHandler"
        @on-update-ip="onUpdateIpHandler"
        @on-open-detail="onOpenDetailHandler"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import {
  ProgressFilter,
  GeneralInfoSummary,
  SummaryCollapse,
  ProgressInfoSummary,
  ProgressInfoHistory,
} from '../shared/components';
import Service from '@/services/api/progress.api';
import DocumentServices from '@/modules/report/document';
import stipulationType from '@/utils/constants/stipulationType.js';

export default {
  components: {
    PageHeader,
    SectionTitle,
    ProgressFilter,
    GeneralInfoSummary,
    SummaryCollapse,
    ProgressInfoSummary,
    ProgressInfoHistory,
  },
  data: () => ({
    academicYears: [],
    selectedAcademicYear: null,
    summary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    progressHistory: [],
    currentProgressSummary: [],
  }),
  computed: {
    stipulationType() {
      return stipulationType.ST42_1;
    },
    partnerStipulationType() {
      return stipulationType.ST42_2;
    },
    pageConfig() {
      return {
        title: 'Informes de Progreso: Estipulación 42.1: PEI No Revisado',
        icon: 'fas fa-calculator',
        routes: [
          'Inicio',
          'Informes de Progreso',
          'Estipulación 42.1: PEI No Revisado',
        ],
      };
    },
    pageActionsConfig() {
      return [
        {
          text: 'Imprimir Anejo',
          icon: 'fas fa-print pr-2',
          variant: 'outline-success',
          visible: true,
          click: () => this.onDownloadAttachment(),
        },
        {
          text: 'Actualizar IP',
          icon: 'fas fa-sync-alt pr-2',
          variant: 'success',
          visible: true,
          click: () => this.onUpdateProgressReportHandler(),
        },
      ];
    },
    actionSummary() {
      return () => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadSummaryHandler(),
          },
        ];
      };
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadHandler(item),
          },
        ];
      };
    },
    isLastIpClosed() {
      if (!this.progressHistory.length) return true;
      return this.progressHistory[0].isClosed;
    },
    lastAcademicYear() {
      if (!this.academicYears?.length) {
        return null;
      }

      return this.academicYears.reduce((max, academicYear) =>
        max.id > academicYear.id ? max : academicYear
      );
    },
    isLastAcademicYearSelected() {
      if (!this.academicYears?.length) {
        return false;
      }

      return this.lastAcademicYear?.id == this.selectedAcademicYear?.id;
    },
  },
  async mounted() {
    await this.getInit();
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
      if (this.academicYears?.length) {
        this.selectedAcademicYear = this.lastAcademicYear;
        await this.getDetail();
      }
    },
    async getAvailableAcademicYears() {
      this.academicYears = (
        await Service.getAcademicYears(this.stipulationType)
      ).data;
    },
    async getDetail() {
      const response = (
        await Service.getDetail({
          stipulationType: this.stipulationType,
          academicYearId: this.selectedAcademicYear.id,
        })
      ).data;
      if (response) {
        this.summary = response.summaries[0];
        this.currentProgressSummary = response.currentSummaryDetail;
        this.progressHistory = response.progressHistory;
      }
    },
    async onRefreshHandler() {
      await this.getDetail();
    },
    onChangeAcademicYearHandler(academicYear) {
      this.selectedAcademicYear = academicYear;
      this.getDetail();
    },
    async onUpdateIpHandler() {
      await Service.updateIp(
        this.stipulationType,
        this.selectedAcademicYear.id,
        true
      ).then((res) => {
        if (res.data) {
          this.onRefreshHandler();
          this.ShowToast(
            'Proceso Exitoso',
            'El IP ha sido actualizado exitosamente.',
            'success'
          );
        } else {
          this.ShowToast(
            'Alerta',
            'No cuenta con datos para actualizar en sistema.',
            'error'
          );
        }
      });
    },
    async onUpdateProgressReportHandler() {
      const response = (
        await Service.isLastIpClosed(
          this.stipulationType,
          this.selectedAcademicYear.id
        )
      ).data;

      if (!response) {
        this.ShowToast(
          'Alerta',
          'Para crear una nueva actualización es necesario cerrar el último IP en Estipulación 42.1 y Estipulación 42.2.',
          'error'
        );
        this.onRefreshHandler();
        return;
      }

      await Service.updateIp(
        this.stipulationType,
        this.selectedAcademicYear.id,
        false
      );
      await Service.updateIp(
        this.partnerStipulationType,
        this.selectedAcademicYear.id,
        false
      );

      this.ShowToast(
        'Proceso Exitoso',
        'El IP ha sido actualizado exitosamente.',
        'success'
      );

      this.onRefreshHandler();
    },
    async onCloseIpHandler({ summaryId }) {
      if (summaryId) {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea congelar los datos y cerrar un IP?',
          'Sí',
          'No'
        ).then(async (res) => {
          if (res) {
            const result = (
              await Service.closeIp(this.stipulationType, summaryId)
            ).data;
            if (result) {
              this.onRefreshHandler();
              this.ShowToast(
                'Proceso Exitoso',
                'El IP ha sido cerrado exitosamente.',
                'success'
              );
            }
          }
        });
      }
    },
    async onDownloadSummaryHandler() {
      if (this.selectedAcademicYear) {
        var academicYearId = this.selectedAcademicYear.id;
        this.onDownloadHandler({ academicYearId, ipSequence: 0 });
      }
    },
    async onDownloadHandler(item) {
      await Service.downloadIpStipulation({
        stipulationType: this.stipulationType,
        ipSequence: item.ipSequence,
        academicYearId: item.academicYearId,
      })
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async onDownloadAttachment() {
      await Service.downloadAttachment(this.stipulationType)
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async onOpenDetailHandler(index) {
      this.progressHistory = await Promise.all(
        this.progressHistory.map(async (item, itemIndex) => {
          if (itemIndex === index) {
            item.isOpen = index === itemIndex ? !item.isOpen : false;
            if (item.isOpen && !item.summary) {
              item.summary = (
                await Service.getIpHistory(
                  this.stipulationType,
                  this.selectedAcademicYear.id,
                  item.ipSequence
                )
              ).data;
            }
          }
          return item;
        })
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/views/progress/shared/scss/common.scss';
</style>
